import {
  Dialog,
  DialogTitle,
  Typography,
  IconButton,
  DialogContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from "@material-ui/core/";
import { Check, Reply, MailOutline, Snooze as SnoozeIconMUI } from "@material-ui/icons";
import { ToggleButton } from "@material-ui/lab";
import clsx from "clsx";
import _ from "lodash";
import moment from "moment-timezone";
import React, { useEffect, useMemo, useState } from "react";
import { isIOS } from "react-device-detect";
import { isEmail, ReactMultiEmail } from "react-multi-email";
import "react-multi-email/style.css";
import { t } from "ttag";
import { Button } from '@coolremote/shared';
import { AlertBell } from "../../icons";
import { useStoreActions, useStoreState } from "../../models/RootStore";
import { SnoozeAlertAction, CancelSnoozeAlertAction } from "../../models/Alerts";
import assetsService from "../../services/assetsService";
import useStyles from "./AlertInfo.style";
import SnoozeDialog from "./components/SnoozeDialog";
import SnoozeStatusBanner from "../../screens/Alerts/components/SnoozeStatusBanner";
import SnoozeIconDesktop from "../../screens/Alerts/components/SnoozeIcon";
import DisconnectedResourcesCount from "../../screens/Alerts/components/DisconnectedResourcesCount";
import AlertUnitsList from "../../screens/Alerts/components/AlertUnitsList";

const AlertInfo: React.FC<any> = (props: any) => {
  const [brand, setBrand] = useState<string>("");
  const getSystem = useStoreState((state) => state.systems.getSystem);
  const setAcknowledgedEvent = useStoreActions((actions) => actions.alerts.setAcknowledgedEvent);
  const { addMessage } = useStoreActions((action) => action.errorMessage);
  const { alert, setAcknowledged, updateAlertLocally, refreshAlerts } = props;
  const { dateFormat, timeFormat } = useStoreState((state: any) => state.users);
  const allSites = useStoreState((state: any) => state.sites.allSites);
  const isAckAlert = alert?.status === "Acknowledged";
  const classes = useStyles({ isAck: isAckAlert });
  const [isEmailDialogOpen, setEmailDialogOpen] = React.useState(false);
  const [emails, setEmails] = React.useState<string[]>([]);
  const [btnIsActive, activateBtn] = React.useState<boolean>(false);
  const [error, setError] = useState(null);
  const [resolvedAlertDescription, setResolvedAlertDescription] = React.useState<any>("");
  const sendAlertEmail = useStoreActions((actions) => actions.alerts.sendAlertEmail);
  const snoozeAlert = useStoreActions((actions) => actions.alerts.snoozeAlert) as SnoozeAlertAction;
  const cancelSnoozeAlert = useStoreActions((actions) => actions.alerts.cancelSnoozeAlert) as CancelSnoozeAlertAction;
  const [snoozeDialogOpen, setSnoozeDialogOpen] = React.useState(false);
  const [snoozeDuration, setSnoozeDuration] = React.useState<string>("1h");
  const [snoozeScope, setSnoozeScope] = React.useState<string>("unit");
  const [snoozeError, setSnoozeError] = React.useState<string>("");
  const types = useStoreState((state) => state.types);
  const { eventTypes, trapPriorityTypes } = types;

  const [isSnoozeExpired, setIsSnoozeExpired] = useState<boolean>(false);

  useEffect(() => {
    if (!alert?.snoozeEndTime) {
      setIsSnoozeExpired(false);
      return;
    }
    const checkExpired = () => {
      const parsedEndTime = moment(alert.snoozeEndTime, [`${dateFormat} ${timeFormat}`, `${dateFormat}   ${timeFormat}`]);
      const expired = parsedEndTime.isValid() ? parsedEndTime.isBefore(moment()) : false;
      setIsSnoozeExpired(expired);
      return expired;
    };
    checkExpired();
    const interval = setInterval(checkExpired, 10000);
    return () => clearInterval(interval);
  }, [alert?.snoozeEndTime, dateFormat, timeFormat]);

  const handleEmailDialog = () => {
    setEmails([]);
    setEmailDialogOpen(!isEmailDialogOpen);
  };
  const handleEmails = (emails: string[]) => {
    setEmails(emails);
  };
  const sendAlertByEmail = () => {
    sendAlertEmail({ emails, id: alert.id })
      .then(() => setEmailDialogOpen(false))
      .catch((error: any) => console.error("error", error));
  };
  const validateEmail = (email: any) => {
    return isEmail(email);
  };
  const addLabeledEmail = (email: string, index: number, removeEmail: (index: number) => void) => {
    return (
      <div data-tag className={classes.emailPart} key={index}>
        {email}
        <span data-tag-handle onClick={() => removeEmail(index)}>
          ×
        </span>
      </div>
    );
  };

  const { permissions } = alert || {};
  const { canAcknowledgeUnAcknowledge, canClose, canShareByEmail, canUpdate } = permissions || {};
  const isBacnetObjectReadingError = alert.type === eventTypes.bacnetObjectReadingError;
  const isBacnetObjectPriorityError = alert.type === eventTypes.bacnetObjectPriorityError;
  const isIndoorOrOutdoorError = alert.type === eventTypes.indoorUnitError || alert.type === eventTypes.outdoorUnitError;
  const isSnoozed = !!alert?.snoozeUsername || !!alert?.snoozeEndTime;
  const isUnitDisAlert = alert?.description?.toUpperCase() === "UNITS DISCONNECTED";
  const alertUnits = isUnitDisAlert ? (alert?.resources || []) : [];

  const handleSnooze = async () => {
    try {
      const payload: any = {
        errorCode: alert.errorCode,
        duration: snoozeDuration,
        triggerEventId: alert.id,
        triggerEventType: alert.type
      };
      if (snoozeScope === 'system') {
        payload.system = alert?.systemId;
      } else {
        payload.unit = alert?.alertItemContainerIds?.unitId;
      }

      const response: any = await snoozeAlert(payload);
      setSnoozeDialogOpen(false);

      if (updateAlertLocally) {
        const timezone = allSites[alert?.alertItemContainerIds?.siteId]?.timezone || moment.tz.guess();
        const durationStr = snoozeDuration.toString();
        const durationVal = parseInt(durationStr, 10);
        const durationUnit = durationStr.endsWith('h') ? 'hours' : durationStr.endsWith('d') ? 'days' : durationStr.endsWith('w') ? 'weeks' : 'minutes';
        const snoozeEndTime = moment().add(durationVal, durationUnit as any).tz(timezone).format(`${dateFormat} ${timeFormat}`);
        updateAlertLocally(alert, {
          snoozeUsername: "You",
          snoozeEndTime,
          snoozeRuleId: response?.id || "mock-rule-id",
          snoozeScope: snoozeScope,
          snoozeNote: "",
          snoozeUnitId: snoozeScope === 'unit' ? alert?.alertItemContainerIds?.unitId : undefined,
          snoozeSystemId: snoozeScope === 'system' ? alert?.systemId : undefined
        });
      }

      if (refreshAlerts) {
        refreshAlerts();
      }
    } catch (err: any) {
      setSnoozeError(err.message);
    }
  };

  const handleCancelSnooze = async (localOnly?: boolean) => {
    try {
      if (!localOnly) {
        await cancelSnoozeAlert({
          id: alert.id,
          snoozeRuleId: alert?.snoozeRuleId
        });
      }
      if (updateAlertLocally) {
        updateAlertLocally(alert, {
          snoozeUsername: "",
          snoozeEndTime: "",
          snoozeRuleId: "",
          snoozeScope: "",
          snoozeNote: "",
          snoozeUnitId: undefined,
          snoozeSystemId: undefined
        });
      }
      if (refreshAlerts) {
        refreshAlerts();
      }
    } catch (err: any) {
      addMessage({ message: err.message });
    }
  };

  const handleAlertAckChange = () => {
    const data = alert.status === "Acknowledged" ? false : true;
    setAcknowledgedEvent({ id: alert.id, data: { value: data } })
      .then(() => {
        setAcknowledged({ id: alert.id, data });
      })
      .catch((err: any) => addMessage({ message: err.message }));
  };

  const getDescription = () => {
    if (isBacnetObjectReadingError) {
      return t`Object %{objectID}(%{title}) is not present. Please go to the BACnet mapping tab and verify the setting for this device objects.`
        .replace('%{objectID}', alert.data?.objectID ?? '')
        .replace('%{title}', alert.data?.title ?? '');
    }
    if (isBacnetObjectPriorityError) {
      return alert.failedOperations[0];
    }
    return alert.errorDescription || alert.description;
  };

  const description = useMemo(() => getDescription(), [getDescription]);

  useEffect(() => {
    if (!brand) {
      const system = getSystem(alert.systemId);
      const brand = system?.brandNum || "";

      setBrand(brand);

    }
    setResolvedAlertDescription("");
    activateBtn(false);
    if (!alert || alert.userText === undefined) { return; }
    setResolvedAlertDescription(alert.userText);
  }, [alert]);

  useEffect(() => {
    if (
      !alert ||
      (alert && alert.status !== "Closed") ||
      (alert && alert.userText === resolvedAlertDescription) ||
      !resolvedAlertDescription
    ) {
      return;
    }

    activateBtn(true);
  }, [resolvedAlertDescription]);
  const isOpenAlert = alert?.status === "Open";
  const isLowLineQualityError = eventTypes.lowLineQualityError === alert.type;
  const showSnoozeButton = isOpenAlert && isIndoorOrOutdoorError && (!isSnoozed || isSnoozeExpired);

  const renderSnoozeStatusBanner = () => {
    if (!isSnoozed) {
      return null;
    }
    return (
      <div style={{ padding: '0 20px', marginBottom: '20px' }}>
        <SnoozeStatusBanner
          username={alert.snoozeUsername}
          scope={alert.snoozeScope}
          endTime={alert.snoozeEndTime}
          onCancel={handleCancelSnooze}
          snoozeNote={alert.snoozeNote}
          isExpired={isSnoozeExpired}
        />
      </div>
    );
  };

  const renderSnoozeButton = () => {
    if (!showSnoozeButton) {
      return null;
    }
    return (
      <Button
        white
        disabled={!canUpdate}
        className={classes.footerSnoozeBtn}
        onClick={() => setSnoozeDialogOpen(true)}
      >
        <SnoozeIconDesktop />
        {t`Snooze Alerts`}
      </Button>
    );
  };

  return (
    <>
      <div className={clsx(classes.infoDialogStyle, { [classes.iosDialogHeight]: isIOS })}>
        <div style={{ overflow: "auto" }}>
          <div id="title" style={{
            display: "flex", alignItems: "center", minHeight: "60px",
            borderBottom: "solid 1px #e5e2e5",
            padding: "0 20px"
          }}>
            <AlertBell style={{ width: 25, height: 25 }} />
            <Typography style={{
              marginLeft: "15px",
              fontFamily: "Roboto",
              fontSize: 20,
              fontWeight: 600,
              color: "#15111f"
            }}>{t`Alert`}{` ${alert.shortId}`}</Typography>
          </div>
          <div className={classes.row} style={{ minHeight: 60 }}>
            <Typography className={classes.alertDetails}>{t`Alert Details`}</Typography>
          </div>
          <div className={clsx(classes.row, alert.unitNames && alert.unitNames.length > 1 && classes.rowMultipleUnits)}>
            <Typography className={classes.title}>
              {alert.unitNames && alert.unitNames.length > 1 ? t`Unit Names` : t`Unit Name`}
            </Typography>
            <div className={classes.namesColumn}>
              {alert.unitNames && alert.unitNames.length > 1 ? (
                alert.unitNames.map((name: string, i: number) => (
                  <Typography key={i} className={clsx(classes.data, classes.cutLongNames)} style={{ display: 'block', textAlign: 'right', marginBottom: i < alert.unitNames.length - 1 ? '4px' : '0px' }}>{name}</Typography>
                ))
              ) : (
            <Typography className={clsx(classes.data, classes.cutLongNames)}>{alert.unitName}</Typography>
              )}
            </div>
          </div>
          <div className={classes.row}>
            <Typography className={classes.title}>{t`Brand`}</Typography>
            <img
              width={100}
              height={25}
              src={assetsService.getBrandLogo(`${brand}-grey.svg`)}
              onError={(event: any) =>
                (event.target.src = assetsService.getBrandLogo("empty.png"))
              }
              alt={brand}
            // className={brandLogo}
            />
          </div>
          <div className={classes.row}>
            <Typography className={classes.title}>{t`Open Date`}</Typography>
            <Typography className={classes.data}>{alert.time}</Typography>
          </div>
          <div className={classes.row}>
            <Typography className={classes.title}>{t`Close Date`}</Typography>
            <Typography className={classes.data}> {alert.status === "Closed" ? alert.clearTime || "" : ""}</Typography>
          </div>
          <div className={classes.row}>
            <Typography className={classes.title}>{t`Error Code`}</Typography>
            <Typography className={classes.data}>{alert.errorCode}</Typography>
          </div>
          {isLowLineQualityError && <div className={classes.row}>
            <Typography className={classes.title}>{t`Device / Line`}</Typography>
            <Typography className={classes.data}>{`${alert.deviceName} / L${alert.lineName}`}</Typography>
          </div>}
          {alert?.priority && <div className={classes.row}>
            <Typography className={classes.title}>{t`Priority`}</Typography>
            <Typography className={classes.data}>{trapPriorityTypes[alert?.priority]}</Typography>
          </div>}
          {isUnitDisAlert &&
            <div className={classes.unitsSection}>
              <Typography className={classes.title}>
                {t`Disconnected Units`} <DisconnectedResourcesCount type={alert.type} resources={alert.resources} />
              </Typography>
              <div className={classes.unitsList}>
                <AlertUnitsList units={alertUnits} />
              </div>
            </div>
          }
          <div style={{ padding: 20, borderBottom: "1px solid #efeff2" }}>
            <Typography className={classes.title}>{t`Description`}</Typography>
            <div>
              {alert.type === eventTypes.bacnetUnitError ? (
                <Typography
                  className={classes.description}
                  style={{ color: alert.data?.color }}
                >
                  {alert.errorDescription}
                </Typography>
              ) : (
                <Typography
                  className={classes.description}
                  dangerouslySetInnerHTML={{
                    __html:
                      alert.styledDescription ||
                      description ||
                      '',
                  }}
                />
              )}
            </div>
          </div>
          {renderSnoozeStatusBanner()}
        </div>
        <div className={classes.snoozeContainer}>
          {renderSnoozeButton()}
        </div>
        <div className={classes.footerButtons}>
          <Button
            disabled={!canShareByEmail}
            variant="contained"
            className={classes.footerShareBtn}
            onClick={() => setEmailDialogOpen(true)}
          >
            <MailOutline style={{ fontSize: "24px" }} />
            {t`Share`}
          </Button>
          <div className={classes.footerRightGroup}>
            {(isOpenAlert || isAckAlert) && (
              <ToggleButton
                disabled={!canAcknowledgeUnAcknowledge}
                value="ack"
                selected={isAckAlert}
                classes={{ root: classes.footerActionBtn, label: classes.footerAckBtn }}
                className={classes.footerAckBtn}
                onChange={handleAlertAckChange}
              >
                <Check className={classes.ackButtonIcon} />
                {t`Acknowledge`}
              </ToggleButton>
            )}
          </div>
        </div>
      </div>
      <Dialog
        open={isEmailDialogOpen}
        aria-labelledby="email-alert-dialog-title"
        aria-describedby="email-alert-dialog-description"
        maxWidth="sm"
        classes={{ paper: classes.emailDialogStyle }}
      >
        <div className={classes.dialogTitle}>
          <DialogTitle id="simple-dialog-title">{t`Share alert`}</DialogTitle>
        </div>
        <div className={classes.emailDialogContent}>
          <Typography>Recipients</Typography>
          <ReactMultiEmail
            placeholder="Email Address/es"
            emails={emails}
            onChange={handleEmails}
            validateEmail={validateEmail}
            getLabel={addLabeledEmail}
          />
          <div className={classes.emailDialogActions}>
            <Button
              white
              width={110}
              marginRight
              onClick={handleEmailDialog}
            >{t`Cancel`}</Button>
            <Button
              type="submit"
              width={110}
              disabled={_.isEmpty(emails)}
              onClick={sendAlertByEmail}
            >{t`Share`}</Button>
          </div>
        </div>
      </Dialog>

      <SnoozeDialog
        open={snoozeDialogOpen}
        onClose={() => setSnoozeDialogOpen(false)}
        snoozeError={snoozeError}
        snoozeDuration={snoozeDuration}
        setSnoozeDuration={setSnoozeDuration}
        snoozeScope={snoozeScope}
        setSnoozeScope={setSnoozeScope}
        handleSnooze={handleSnooze}
        alert={alert}
      />
    </>
  );
};
export default AlertInfo;
